import type React from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { STAGING_STYLES } from '@/lib/roomConfig';
import { cn } from '@/lib/utils';
import type { StagingStyle } from './TryoutStep2Configure';

const STYLE_PREVIEWS: Record<StagingStyle, string> = {
  modern: '/tryout/styles/modern.webp',
  luxury: '/tryout/styles/luxury.jpg',
  industrial: '/tryout/styles/industrial.jpg',
};

interface TryoutStylePickerProps {
  value: StagingStyle;
  onChange: (s: StagingStyle) => void;
  /**
   * 'grid' — three large preview cards in a row (full-width contexts).
   * 'list' — compact vertical rows (thumbnail + label), for a side column
   *          where the style choice must sit next to the room image without
   *          pushing the "Staging erstellen" button below the fold.
   */
  variant?: 'grid' | 'list';
}

export const TryoutStylePicker: React.FC<TryoutStylePickerProps> = ({ value, onChange, variant = 'grid' }) => {
  if (variant === 'list') {
    return (
      <div className="flex flex-col gap-2" style={{ fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}>
        {STAGING_STYLES.map((s) => {
          const active = value === (s.id as StagingStyle);
          return (
            <button
              key={s.id}
              onClick={() => onChange(s.id as StagingStyle)}
              aria-pressed={active}
              className={cn(
                'group flex items-center gap-3 w-full text-left rounded-[12px] p-1.5 pr-3 border transition-all duration-150',
                active
                  ? 'border-[#FE6F17] bg-[#FFF3EC] ring-[3px] ring-[#FE6F17]/15'
                  : 'border-[#EBEBE6] bg-white hover:border-[#FE6F17]/50',
              )}
            >
              <div className="w-[88px] aspect-[4/3] flex-shrink-0 overflow-hidden rounded-[9px] bg-[#F5F4F0]">
                <img
                  src={STYLE_PREVIEWS[s.id as StagingStyle]}
                  alt={s.label}
                  draggable={false}
                  className={cn(
                    'w-full h-full object-cover transition-transform duration-300',
                    active ? 'scale-[1.04]' : 'group-hover:scale-[1.04]',
                  )}
                />
              </div>
              <span
                className={cn(
                  'flex-1 text-[15px] font-semibold transition-colors',
                  active ? 'text-[#FE6F17]' : 'text-[#001116] group-hover:text-[#FE6F17]',
                )}
              >
                {s.label}
              </span>
              {active && <CheckIcon className="w-5 h-5 text-[#FE6F17] flex-shrink-0" strokeWidth={2.5} />}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-2.5" style={{ fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}>
      {STAGING_STYLES.map((s) => {
        const active = value === (s.id as StagingStyle);
        return (
          <button key={s.id} onClick={() => onChange(s.id as StagingStyle)}
            className="group text-left bg-transparent border-0 p-0" aria-pressed={active}>
            <div className={cn('aspect-[4/3] w-full overflow-hidden rounded-[12px] bg-[#F5F4F0] transition-all duration-150',
              active ? 'ring-[3px] ring-[#FE6F17]' : 'ring-1 ring-[#EBEBE6] group-hover:ring-[#FE6F17]/50')}>
              <img src={STYLE_PREVIEWS[s.id as StagingStyle]} alt={s.label} draggable={false}
                className={cn('w-full h-full object-cover transition-transform duration-300',
                  active ? 'scale-[1.04]' : 'group-hover:scale-[1.04]')} />
            </div>
            <div className={cn('pt-2.5 px-0.5 text-[14px] font-semibold transition-colors',
              active ? 'text-[#FE6F17]' : 'text-[#001116] group-hover:text-[#FE6F17]')}>{s.label}</div>
          </button>
        );
      })}
    </div>
  );
};
